/**
 * @file company.schema.ts
 * @description Empresa: a camada opcional dentro da carteira de uma marca.
 *
 * ## O que a empresa NÃO é
 *
 * Não é um novo tipo de usuário e não é um sub-parceiro. Uma marca de
 * benefícios corporativos atende empresas; cada empresa tem gestores que
 * administram os próprios colaboradores; e **colaborador é paciente** — o mesmo
 * `PREGNANT_WOMAN` de sempre, agora com `companyId`. Modelar colaborador como
 * um quarto papel duplicaria prontuário, jornada e conteúdo.
 *
 * Antes disso, uma marca que administrava várias empresas só podia modelá-las
 * criando um `Partner` por empresa, sem hierarquia — o que multiplicava
 * configuração de identidade visual, catálogo e rede para cada cliente do
 * cliente.
 *
 * ## O princípio inegociável
 *
 * O gestor administra **elegibilidade e ativação**, nunca dado clínico. Não há
 * campo aqui que exponha saúde, e as consultas que servem o gestor devolvem
 * contagens e status de convite — nunca fase de gestação, conteúdo consumido ou
 * conversa. Isso é LGPD, e é também o argumento de venda para o RH.
 */

import { z } from 'zod';
import { brandIdField } from './brand';

/**
 * Provedores de e-mail abertos ao público. Nenhum deles é domínio de empresa:
 * cadastrar `gmail.com` como domínio liberaria o link validado para qualquer
 * pessoa. A lista cresce quando aparecer caso novo.
 */
export const PROVEDORES_PUBLICOS: readonly string[] = [
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.com.br', 'outlook.com', 'outlook.com.br', 'live.com',
  'msn.com', 'yahoo.com', 'yahoo.com.br', 'ymail.com', 'icloud.com', 'me.com', 'mac.com', 'aol.com',
  'uol.com.br', 'bol.com.br', 'terra.com.br', 'ig.com.br', 'globo.com', 'globomail.com', 'r7.com',
  'zipmail.com.br', 'protonmail.com', 'proton.me', 'gmx.com', 'zoho.com', 'yandex.com', 'mail.com',
];

/** `@Empresa.com.br ` → `empresa.com.br`. Aceita domínio ou e-mail inteiro. */
export function normalizarDominio(valor: string): string {
  const v = valor.trim().toLowerCase();
  return (v.includes('@') ? v.slice(v.lastIndexOf('@') + 1) : v).replace(/^\.+|\.+$/g, '');
}

export function dominioEhPublico(dominio: string): boolean {
  return PROVEDORES_PUBLICOS.includes(normalizarDominio(dominio));
}

/**
 * Problemas de uma lista de domínios, para a escrita recusar com motivo.
 * Vazio = pode gravar.
 */
export function validarDominios(dominios: string[]): string[] {
  const problemas: string[] = [];
  for (const bruto of dominios) {
    const d = normalizarDominio(bruto);
    if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(d)) problemas.push(`"${bruto}" não é um domínio de e-mail.`);
    else if (dominioEhPublico(d)) problemas.push(`${d} é um provedor público de e-mail e não identifica uma empresa.`);
  }
  return problemas;
}

/** O e-mail é de um dos domínios da empresa? */
/**
 * O campo de domínios, igual na empresa e na marca (A062, fase D): na leitura,
 * valor sujo ou provedor público é descartado em vez de derrubar a listagem.
 */
export function dominiosField() {
  return z.preprocess(
    (raw) => (Array.isArray(raw) ? raw.map((d) => normalizarDominio(String(d))).filter((d) => d && !dominioEhPublico(d)) : raw),
    z.array(z.string()).default([])
  );
}

export function emailEhDaEmpresa(email: string, dominios: readonly string[]): boolean {
  const d = normalizarDominio(email);
  return !dominioEhPublico(d) && dominios.map(normalizarDominio).includes(d);
}

export const CompanyStatusEnum = z.enum(['active', 'inactive', 'onboarding']);
export type CompanyStatus = z.infer<typeof CompanyStatusEnum>;

/**
 * Identidade visual própria da empresa.
 *
 * Fica de fora do MVP: a decisão de se uma empresa pode ter marca própria é da
 * MARCA (campo `allowsCompanyBranding` no documento dela), não da empresa. O
 * campo nasce opcional para que ligar isso depois não seja migração de schema.
 */
const CompanyBrandingSchema = z.object({
  logoUrl: z.string().url().or(z.literal('')).nullable().optional(),
  colors: z
    .object({
      primary: z.string(),
      secondary: z.string(),
    })
    .optional(),
});

export const CompanySchema = z.object({
  id: z.string(),

  /** Marca dona da carteira. Toda empresa pertence a exatamente uma marca. */
  brandId: brandIdField(),

  name: z.string().min(1),
  /** CNPJ. Opcional porque o cadastro inicial costuma vir só com o nome. */
  document: z.string().optional(),
  status: CompanyStatusEnum.default('onboarding'),

  /**
   * Código que a colaboradora usa para se vincular à empresa no cadastro.
   *
   * Alternativa à lista de CPFs: uma empresa que não quer (ou não pode)
   * entregar a base de CPFs distribui o código internamente. Os dois caminhos
   * levam ao mesmo lugar — carimbar `companyId` no cadastro.
   */
  activationCode: z.string().optional(),

  branding: CompanyBrandingSchema.optional(),

  /**
   * Domínios de e-mail da empresa, que o link validado aceita (A062).
   *
   * Confirmados pelo admin da marca — a gestora sugere o dela, mas pode ser
   * de corretora ou RH terceirizado. Provedor público nunca entra: na
   * escrita, `validarDominios` recusa; na leitura, valor sujo é descartado
   * em vez de derrubar a listagem (mesma regra do `brandFeaturesField`).
   */
  dominios: dominiosField(),

  /** Contato do RH responsável, para o time da marca saber com quem falar. */
  contactName: z.string().optional(),
  contactEmail: z.string().email().or(z.literal('')).optional(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().default(null),
});

export type Company = z.infer<typeof CompanySchema>;

/**
 * Situação de uma colaboradora no funil de ativação.
 *
 * É o único "estado de pessoa" que o gestor enxerga. Note que nenhum destes
 * valores diz nada sobre saúde — o funil para em "ativa".
 */
export const CollaboratorStatusEnum = z.enum([
  /** CPF na lista, ainda sem cadastro. */
  'ELIGIBLE',
  /** Convite enviado. */
  'INVITED',
  /** Cadastro concluído. */
  'ACTIVE',
  /** Saiu da empresa: perdeu a elegibilidade, mas a conta continua existindo. */
  'REMOVED',
]);
export type CollaboratorStatus = z.infer<typeof CollaboratorStatusEnum>;

/**
 * Elegibilidade de uma pessoa numa empresa.
 *
 * Coleção separada de `users` de propósito: a elegibilidade existe ANTES da
 * pessoa se cadastrar (é uma lista de CPFs que o RH sobe) e continua existindo
 * depois que ela sai. Guardar isso no usuário exigiria criar usuário sem
 * cadastro — e um documento de pessoa que nunca consentiu.
 */
export const CompanyMemberSchema = z.object({
  id: z.string(),
  companyId: z.string(),
  brandId: brandIdField(),

  /** Chave de vínculo. É por CPF que o cadastro encontra a elegibilidade. */
  cpf: z.string(),
  /** Preenchido quando a pessoa se cadastra. Antes disso é `null`. */
  userId: z.string().nullable().default(null),

  status: CollaboratorStatusEnum.default('ELIGIBLE'),

  /** Nome como veio da lista do RH, para conferência antes do cadastro. */
  nameFromEmployer: z.string().optional(),

  invitedAt: z.coerce.date().nullable().default(null),
  activatedAt: z.coerce.date().nullable().default(null),
  /**
   * Quando deixou de ser elegível.
   *
   * Remoção é escrita, não apagamento: reverter é limpar este campo. Apagar o
   * documento perderia o histórico de que a pessoa esteve na empresa, que é o
   * que sustenta faturamento por vidas.
   */
  removedAt: z.coerce.date().nullable().default(null),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type CompanyMember = z.infer<typeof CompanyMemberSchema>;

/** A elegibilidade está valendo agora? */
export function isCompanyMemberEligible(
  member: Pick<CompanyMember, 'status' | 'removedAt'>
): boolean {
  return !member.removedAt && member.status !== 'REMOVED';
}
